import { getSiteTheme } from './settings';

// Nápady pro AI (rychlé volby v okně „Celá samolepka s AI“) podle tématu webu (/pejsci, /kocicky …).
// Pořadí: vlastní nápady z Administrace → Témata, výchozí nápady podle adresy tématu, obecné nápady hlavní adresy.
export type SetIdea = { label: string; items: string };
export type AiIdeas = { ideas: string[]; sets: SetIdea[]; photoHint: string; themed: boolean };

const GENERIC: AiIdeas = {
  ideas: ['dinosaurus s fotbalovým míčem', 'jednorožec na duze', 'liška v lese', 'raketa ve vesmíru', 'kočička s mašlí', 'traktor na poli'],
  sets: [
    { label: 'Lesní zvířátka', items: 'liška, ježek, sova, veverka' },
    { label: 'Dinosauři', items: 'tyranosaurus, triceratops, brontosaurus, pterodaktyl' },
    { label: 'Auta a stroje', items: 'hasičské auto, traktor, bagr, vláček' },
    { label: 'Vesmír', items: 'raketa, astronaut, planeta Saturn, mimozemšťan' },
    { label: 'Moře', items: 'velryba, chobotnice, želva, rybička' },
    { label: 'Farma', items: 'kravička, prasátko, ovečka, kohoutek' },
  ],
  photoHint: 's fotbalovým míčem, v modrém tričku',
  themed: false,
};

const BY_THEME: { re: RegExp; v: Omit<AiIdeas, 'themed'> }[] = [
  { re: /v[aá]no|christ|xmas|weihn|navid|natal|kar[aá]cs|swi[eę]t|boze|bożе/i, v: {
    ideas: ['sobík se šálou', 'perníček s polevou', 'sněhulák s mrkví', 'vánoční stromeček s dárky', 'Santa na saních', 'tučňák s čepicí'],
    sets: [
      { label: 'Vánoční cukroví', items: 'perníček, vanilkový rohlíček, linecké srdíčko, včelí úl' },
      { label: 'Zimní zvířátka', items: 'sobík, tučňák, lední medvěd, liška ve sněhu' },
      { label: 'Vánoční ozdoby', items: 'baňka, hvězda, zvoneček, cukrová hůlka' },
      { label: 'Vánoční postavy', items: 'Santa, sněhulák, anděl, skřítek' },
    ],
    photoHint: 's vánoční čepicí, se šálou',
  } },
  { re: /hallow|dýn|dyn[eě]|strasid|strašid/i, v: {
    ideas: ['dýně se svíčkou', 'roztomilý duch', 'černá kočka na koštěti', 'netopýr u měsíce', 'čarodějnice s kotlíkem', 'strašidelný hrad'],
    sets: [
      { label: 'Strašidla', items: 'roztomilý duch, upírek, mumie, kostlivec' },
      { label: 'Dýně', items: 'usměvavá dýně, dýně s kloboukem, dýně se svíčkou, dýně s mašlí' },
      { label: 'Noční zvířata', items: 'netopýr, sova, černá kočka, pavouček' },
      { label: 'Čarodějnice', items: 'koště, kotlík, čarodějnický klobouk, kouzelná hůlka' },
    ],
    photoHint: 'v kostýmu ducha, s dýní',
  } },
  { re: /velikon|easter|ostern|wielkanoc|pascu|h[uú]sv[eé]t/i, v: {
    ideas: ['zajíček s vajíčkem', 'kuřátko ve skořápce', 'beránek s mašlí', 'malovaná kraslice', 'pomlázka s pentlemi', 'košík s kraslicemi'],
    sets: [
      { label: 'Velikonoční zvířátka', items: 'zajíček, kuřátko, beránek, kachnička' },
      { label: 'Kraslice', items: 'modrá kraslice, červená kraslice, zelená kraslice, žlutá kraslice' },
      { label: 'Jaro', items: 'tulipán, sněženka, motýl, beruška' },
    ],
    photoHint: 's králičíma ušima, s košíkem',
  } },
  { re: /pejs|\bps[iy]\b|\bpes\b|dog|hund|kutya|perr|\bcan[ei]\b|\bpies/i, v: {
    ideas: ['štěně s míčkem', 'jezevčík v klobouku', 'dalmatin s mašlí', 'pejsek s kostičkou', 'labrador ve sluchátkách', 'buldoček na skateboardu'],
    sets: [
      { label: 'Štěňátka', items: 'štěně labradora, štěně jezevčíka, štěně dalmatina, štěně huskyho' },
      { label: 'Pejsci a hračky', items: 'pejsek s míčkem, pejsek s kostičkou, pejsek s frisbee, pejsek s plyšákem' },
      { label: 'Plemena', items: 'jezevčík, buldoček, kokršpaněl, border kolie' },
      { label: 'Pejsci v kostýmech', items: 'pejsek astronaut, pejsek hasič, pejsek pirát, pejsek kuchař' },
    ],
    photoHint: 's míčkem, s obojkem a jmenovkou',
  } },
  { re: /koc|koč|kot[eě]|kitt|\bcat|katz|macsk|\bgat[ot]/i, v: {
    ideas: ['koťátko s klubíčkem', 'kočička s mašlí', 'mourek na polštáři', 'koťátko v košíku', 'kočička s rybičkou', 'kocour v kloboučku'],
    sets: [
      { label: 'Koťátka', items: 'mourovaté koťátko, černé koťátko, zrzavé koťátko, bílé koťátko' },
      { label: 'Kočičky a hračky', items: 'kočička s klubíčkem, kočička s myškou, kočička s rybičkou, kočička v krabici' },
      { label: 'Plemena', items: 'perská kočka, siamská kočka, mainská mývalí, britská modrá' },
      { label: 'Kočičky v kostýmech', items: 'kočička princezna, kočička čarodějnice, kočička astronautka, kočička kuchařka' },
    ],
    photoHint: 's mašlí, s klubíčkem',
  } },
  { re: /dino|saur/i, v: {
    ideas: ['tyranosaurus s fotbalovým míčem', 'triceratops na skateboardu', 'dinosauří mládě ve vajíčku', 'brontosaurus v džungli', 'stegosaurus s balónky', 'pterodaktyl nad sopkou'],
    sets: [
      { label: 'Dinosauři', items: 'tyranosaurus, triceratops, brontosaurus, pterodaktyl' },
      { label: 'Dinosauří mláďata', items: 'mládě tyranosaura, mládě triceratopse, mládě stegosaura, mládě brontosaura' },
      { label: 'Dinosauři a sport', items: 'dinosaurus s míčem, dinosaurus na kole, dinosaurus plavec, dinosaurus hokejista' },
      { label: 'Pravěk', items: 'sopka, dinosauří vejce, kapradí, pterodaktyl' },
    ],
    photoHint: 'jako dinosaurus, s dinosaurem',
  } },
];

let cache: AiIdeas | null = null;
export function getAiIdeas(): AiIdeas {
  if (cache) return cache;
  const t = getSiteTheme() as any;
  if (!t) return (cache = GENERIC);
  const own = (t.ai || {}) as { ideas?: string[]; sets?: SetIdea[]; photoHint?: string };
  const def = BY_THEME.find(x => x.re.test(`${t.slug} ${t.name || ''}`))?.v;
  const ideas = own.ideas?.length ? own.ideas : def?.ideas;
  const sets = own.sets?.length ? own.sets : def?.sets;
  if (!ideas && !sets) return (cache = GENERIC);
  return (cache = {
    ideas: ideas || GENERIC.ideas,
    sets: sets || GENERIC.sets,
    photoHint: own.photoHint || def?.photoHint || GENERIC.photoHint,
    themed: true,
  });
}
/** Výchozí nápady pro téma podle jeho adresy/názvu (pro administraci). */
export function defaultAiIdeasFor(slug: string, name = ''): Omit<AiIdeas, 'themed'> | null {
  return BY_THEME.find(x => x.re.test(`${slug} ${name}`))?.v || null;
}
